import { onScopeDispose, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { toast } from 'vue-sonner'
import type Echo from 'laravel-echo'
import {
  GMC_ACCOUNTS_FETCHED_EVENT,
  gmcAccountsQueryKey,
  merchantQueryKey,
} from '@/features/merchants/queries/merchantsCache'
import type { GmcAccountsFetchedEvent } from '@/features/merchants/types/merchant'
import { toNotification } from '@/features/notifications/api/get-notifications'
import { useOpenNotification } from '@/features/notifications/composables/useOpenNotification'
import {
  NOTIFICATION_CREATED_EVENT,
  NOTIFICATIONS_QUERY_KEY,
  notificationChannel,
} from '@/features/notifications/queries/keys'
import type { NotificationCreatedEvent } from '@/features/notifications/types/notification'
import { ORGANIZATION_ONBOARDING_QUERY_KEY } from '@/features/organizations/queries/useOrganizationOnboarding'
import { createEcho } from '@/lib/realtime/echo'
import { useAuthStore } from '@/stores/auth'

const WEBSITE_NOTIFICATION_PREFIX = 'website.'

/**
 * Keeps one socket connection on the signed-in user's own channel for as long as they stay signed
 * in. New notifications show as a toast and refresh the bell badge and lists.
 */
export function useNotificationSocket(): void {
  const auth = useAuthStore()
  const queryClient = useQueryClient()
  const router = useRouter()
  const { t } = useI18n()
  const openNotification = useOpenNotification()

  let echo: Echo<'reverb'> | null = null

  function refreshNotifications(): void {
    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
  }

  function refreshOnboarding(): void {
    queryClient.invalidateQueries({ queryKey: ORGANIZATION_ONBOARDING_QUERY_KEY })
  }

  function onNotificationCreated(event: NotificationCreatedEvent): void {
    if ('errors' in event) {
      console.warn('Notification socket event failed', event.errors)
      return
    }

    const notification = toNotification(event.data)
    const route = notification.route

    refreshNotifications()
    // Staff moving a website along is what advances the onboarding steps.
    if (notification.type.startsWith(WEBSITE_NOTIFICATION_PREFIX)) refreshOnboarding()
    toast[notification.severity](notification.title, {
      description: notification.body ?? undefined,
      action:
        route && router.hasRoute(route.name)
          ? {
              label: t('notifications.toast.open'),
              onClick: () => openNotification(notification),
            }
          : undefined,
    })
  }

  /**
   * Only updates the account picker. The same fetch also sends `notification.created`, which is what
   * tells the user, so nothing here may toast or touch the bell.
   */
  function onGmcAccountsFetched(event: GmcAccountsFetchedEvent): void {
    if ('errors' in event) {
      console.warn('GMC accounts socket event failed', event.errors)
      return
    }

    const merchantId = event.data.merchant_id

    queryClient.invalidateQueries({ queryKey: merchantQueryKey(merchantId) })
    queryClient.invalidateQueries({ queryKey: gmcAccountsQueryKey(merchantId) })
  }

  function disconnect(): void {
    echo?.disconnect()
    echo = null
  }

  function connect(userId: string): void {
    echo = createEcho()
    if (!echo) return

    // The server doesn't replay events, so catch up on what a dropped connection missed.
    let hasConnected = false
    echo.connector.pusher.connection.bind('connected', () => {
      if (hasConnected) {
        refreshNotifications()
        refreshOnboarding()
      }
      hasConnected = true
    })

    echo
      .private(notificationChannel(userId))
      .listen(NOTIFICATION_CREATED_EVENT, onNotificationCreated)
      .listen(GMC_ACCOUNTS_FETCHED_EVENT, onGmcAccountsFetched)
      .error((error: unknown) => console.warn('Notification socket subscription failed', error))
  }

  // The id, not `isAuthenticated`: signing in over an existing session must switch channels.
  watch(
    () => auth.user?.id,
    (userId) => {
      disconnect()
      if (userId) connect(userId)
    },
    { immediate: true },
  )

  onScopeDispose(disconnect)
}
